/**
 * AlertStatusStrip (H3 / D5) — the greeting-row status element, alert-aware.
 * Replaces the power-only pill: instead of a green "Power on" that lies while a
 * demand near-miss or vacancy anomaly is active, it surfaces the highest-severity
 * active condition (tone-coloured, linked) and an overflow count. All-clear keeps
 * the reassuring green "Power on".
 *
 * Ranking + labels come from the shared route-free `resolveHomeAlerts`
 * (packages/shared-ui) so mobile consumes the same source in Phase 3; this
 * component owns only the WEB kind->route mapping and presentation. Atlas: B1.
 */
"use client";

import type { ComponentType } from "react";
import Link from "next/link";
import { Zap, ZapOff, AlertTriangle, CloudLightning } from "lucide-react";
import { resolveHomeAlerts, type HomeAlertKind, type HomeAlertInputs } from "@petci/shared-ui";
import { useT } from "@/lib/locale";

const KIND_ICON: Record<HomeAlertKind, ComponentType<{ size?: number; className?: string; "aria-hidden"?: boolean }>> = {
  storm: CloudLightning,
  outage: ZapOff,
  demand: AlertTriangle,
  anomaly: AlertTriangle,
};

/** Web route for a condition kind. Mobile owns its own mapping in Phase 3. */
function routeFor(kind: HomeAlertKind, ref?: string): string {
  switch (kind) {
    case "storm":
      return "/hurricane";
    case "outage":
      return ref ? `/outage/${ref}` : "/outage/map";
    case "demand":
      return "/hotel/portfolio";
    case "anomaly":
      return ref ? `/pm/anomaly/${ref}` : "/pm";
  }
}

export function AlertStatusStrip({ inputs }: { inputs: HomeAlertInputs }) {
  const t = useT();
  const { top, count } = resolveHomeAlerts(inputs);

  if (!top) {
    return (
      <span className="inline-flex items-center gap-2 rounded-full bg-semantic-success/10 text-semantic-success-text px-3 py-1.5 text-sm font-medium">
        <Zap size={14} aria-hidden />
        {t("home.powerOn")}
      </span>
    );
  }

  const Icon = KIND_ICON[top.kind];
  const label = t(top.labelKey, top.params);
  // Brief format for the accessible name: "1 alert — Demand near threshold".
  const countLabel = t(count === 1 ? "home.alert.countOne" : "home.alert.countOther", { count });
  const toneCls =
    top.tone === "critical"
      ? "bg-semantic-error/10 text-semantic-error hover:bg-semantic-error/20"
      : "bg-semantic-warning/10 text-semantic-warning-text hover:bg-semantic-warning/20";

  return (
    <Link
      href={routeFor(top.kind, top.ref)}
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium transition-colors duration-fast ${toneCls}`}
      aria-label={`${countLabel} — ${label}`}
    >
      <Icon size={14} aria-hidden />
      <span className="truncate max-w-[11rem]">{label}</span>
      {count > 1 && (
        <span className="text-xs font-semibold tabular-nums opacity-70" aria-hidden>
          +{count - 1}
        </span>
      )}
    </Link>
  );
}
